import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AsideSection } from "@/components/AsideSection";
import { toast } from "sonner";
import { checkPassword, PASSWORD_RULE } from "@/lib/password";
import { MarketingActivityCard } from "@/components/email/MarketingActivityCard";
import { RoleViewsTab } from "@/components/RoleViewsTab";
import {
  ShieldCheck, Users as UsersIcon, Loader2, X, Plus, Copy, ChevronDown, ChevronRight,
  CheckCircle2, Ban, Pencil, Info, Trash2, Smartphone,
} from "lucide-react";

const PW_PLACEHOLDER = "Htss!Role2026";

function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-200"} ${disabled ? "opacity-50" : ""}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

import { WebMfaSettings } from "@/components/auth/WebMfaSettings";
import { MfaProgress } from "@/components/auth/MfaProgress";

export default function UsersRoles() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [viewPerms, setViewPerms] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [mfaUser, setMfaUser] = useState(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, p] = await Promise.all([
        api.get("/admin/users"), api.get("/admin/roles"), api.get("/admin/permissions"),
      ]);
      setUsers(u.data.users); setRoles(r.data.roles); setGroups(p.data.groups);
    } catch (e) {
      toast.error("Could not load users & roles");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleName = (k) => roles.find((r) => r.key === k)?.name || k;

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="users-roles-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Staff &amp; Roles</h1>
          <p className="text-sm text-slate-500">Manage staff accounts, role assignments and permissions.</p>
        </div>
        {tab === "users" && (
          <Button onClick={() => setEditUser({ email: "", name: "", roles: [], active: true, customer_id: "", _new: true })}
            className="ml-auto gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ur-add-user">
            <Plus className="w-4 h-4" /> New staff
          </Button>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {[["users", "Staff"], ["roles", "Roles"], ["views", "Role views"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ur-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 ${tab === k ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "users" && (
        <div>
          {me.platform_role === "operix_admin" && <WebMfaSettings users={users} />}
          <MfaProgress users={users} onlyMissing={onlyMissing} setOnlyMissing={setOnlyMissing} />
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Roles</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Authenticator</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {users.filter((u) => !onlyMissing || (u.active && !u.mfa_enabled)).map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`ur-user-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                    <td className="px-4 py-3 text-slate-500">{u.email}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{u.roles.map((r) => (
                      <span key={r} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{roleName(r)}</span>
                    ))}</div></td>
                    <td className="px-4 py-3">{u.active
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Active</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Ban className="w-3.5 h-3.5" /> Inactive</span>}</td>
                    <td className="px-4 py-3" data-testid={`ur-mfa-${u.email}`}>{u.mfa_enabled
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Smartphone className="w-3.5 h-3.5" /> Set up</span>
                      : <span className="text-xs text-amber-600">Not set up yet</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setViewPerms(u)} data-testid={`ur-perms-${u.email}`}>Permissions</Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setEditUser({ ...u, customer_id: u.customer_id || "" })} data-testid={`ur-edit-${u.email}`}><Pencil className="w-3.5 h-3.5" /> Edit</Button>
                        {u.mfa_enabled && (
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setMfaUser(u)} data-testid={`ur-mfa-reset-${u.email}`}><Smartphone className="w-3.5 h-3.5" /> Reset authenticator</Button>
                        )}
                        {u.id !== me.id && (
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDelUser(u)} data-testid={`ur-delete-${u.email}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "views" && <RoleViewsTab roles={roles} />}

      {tab === "roles" && (
        <div className="space-y-3">
          {roles.map((r) => (
            <RoleCard key={r.key} role={r} groups={groups} onEdit={() => setEditRole(r)}
              onCopy={async () => { await api.post(`/admin/roles/${r.key}/copy`); toast.success("Role copied"); load(); }} />
          ))}
        </div>
      )}

      {editUser && <UserModal user={editUser} roles={roles} me={me}
        onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
      {mfaUser && <ResetMfaModal user={mfaUser}
        onClose={() => setMfaUser(null)} onDone={() => { setMfaUser(null); load(); }} />}
      {delUser && <DeleteUserModal user={delUser}
        onClose={() => setDelUser(null)} onDeleted={() => { setDelUser(null); load(); }} />}
      {viewPerms && <PermsModal user={viewPerms} roleName={roleName} onClose={() => setViewPerms(null)} />}
      {editRole && <RoleModal role={editRole} groups={groups}
        onClose={() => setEditRole(null)} onSaved={() => { setEditRole(null); load(); }} />}
    </div>
  );
}

function ResetMfaModal({ user, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/mfa/reset`); toast.success("Authenticator reset"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset"); setBusy(false); }
  };
  return (
    <ModalShell title="Reset authenticator" onClose={onClose} testid="ur-mfa-reset-modal"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={reset} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ur-mfa-reset-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Reset</Button></>}>
      <p className="text-sm text-slate-600">Remove the authenticator for <b>{user.email}</b>? Their old codes and backup codes stop working, and they'll scan a new QR code the next time they sign in.</p>
    </ModalShell>
  );
}

function DeleteUserModal({ user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/admin/users/${user.id}`); toast.success("User deleted"); onDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); setBusy(false); }
  };
  return (
    <ModalShell title="Delete staff member" onClose={onClose} testid="ur-delete-modal"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={del} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="ur-delete-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Delete</Button></>}>
      <p className="text-sm text-slate-600">Permanently remove the login account <b>{user.email}</b> ({user.name})? They will no longer be able to sign in. This cannot be undone.</p>
    </ModalShell>
  );
}

function RoleCard({ role, groups, onEdit, onCopy }) {
  const [open, setOpen] = useState(false);
  const has = (k) => role.is_all || role.permissions.includes(k);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl" data-testid={`ur-role-${role.key}`}>
      <div className="flex items-center gap-3 p-4">
        <button onClick={() => setOpen((v) => !v)} className="text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <div className="flex-1">
          <div className="flex items-center gap-2"><span className="font-bold text-slate-900">{role.name}</span>
            {role.system && <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">System</span>}
            {role.data_scope !== "all" && <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{role.data_scope}</span>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
        </div>
        <span className="text-xs text-slate-400">{role.is_all ? "All" : role.permission_count} perms</span>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={onCopy} data-testid={`ur-copy-${role.key}`}><Copy className="w-3.5 h-3.5" /> Copy</Button>
        {role.key !== "super_admin" && <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={onEdit} data-testid={`ur-editrole-${role.key}`}><Pencil className="w-3.5 h-3.5" /> Edit</Button>}
      </div>
      {open && (
        <div className="border-t border-slate-100 p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => {
            const on = g.permissions.filter((p) => has(p.key));
            if (!on.length) return null;
            return (
              <div key={g.key}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1">{g.label}{g.financial && <span className="text-rose-400">·finance</span>}</div>
                <ul className="mt-1 space-y-0.5">{on.map((p) => <li key={p.key} className="text-xs text-slate-600">{p.label}</li>)}</ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModalShell({ title, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[1px] flex justify-end" onClick={onClose} data-testid={testid}>
      <div className="relative bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-hidden flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-1 ring-slate-200 transition-colors hover:bg-slate-50 focus:outline-none">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center px-6 py-5 border-b border-slate-100 pr-16">
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-white">{footer}</div>}
      </div>
    </div>
  );
}

function UserModal({ user, roles, me, onClose, onSaved }) {
  const [form, setForm] = useState({ ...user });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const assignable = roles.filter((r) => !r.hidden && (me.is_super ? true : (r.staff_assignable && !["super_admin", "customer"].includes(r.key))));
  const toggleRole = (k) => set("roles", form.roles.includes(k) ? form.roles.filter((x) => x !== k) : [...form.roles, k]);
  const isCustomer = form.roles.includes("customer");

  const save = async () => {
    const email = (form.email || "").trim();
    if (!(form.name || "").trim()) { toast.error("Enter a name"); return; }
    if (form._new && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { toast.error("Enter a valid email address, e.g. name@company.co.uk"); return; }
    if (!form.roles.length) { toast.error("Select at least one role"); return; }
    if (form.password) {
      const pwErr = checkPassword(form.password);
      if (pwErr) { toast.error(pwErr); return; }
    }
    setBusy(true);
    try {
      const body = { email, name: form.name.trim(), roles: form.roles, active: form.active, customer_id: form.customer_id || null };
      if (form.password) body.password = form.password;
      if (form._new) { body.password = form.password || PW_PLACEHOLDER; await api.post("/admin/users", body); }
      else await api.put(`/admin/users/${form.id}`, body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={form._new ? "New staff member" : "Edit staff member"} onClose={onClose} testid="ur-user-modal"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ur-user-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button></>}>
      <AsideSection first icon={Info} title="Account details" description="Basic information for this staff member.">
        <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Name</span>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="ur-user-name" /></label>
        <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</span>
          <Input value={form.email} disabled={!form._new} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="ur-user-email" /></label>
        <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{form._new ? "Password" : "Reset password (optional)"}</span>
          <Input type="text" value={form.password || ""} onChange={(e) => set("password", e.target.value)} placeholder={form._new ? PW_PLACEHOLDER : "leave blank to keep"} className="mt-1" data-testid="ur-user-password" />
          <span className="mt-1 block text-[11px] text-slate-500">{PASSWORD_RULE}</span></label>
      </AsideSection>
      <AsideSection icon={ShieldCheck} title="Roles & access" description="Choose the roles that grant this user access. Toggle the account active or inactive.">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Roles</span>
          <div className="mt-1 grid grid-cols-2 gap-1.5">
            {assignable.map((r) => (
              <label key={r.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-sm ${form.roles.includes(r.key) ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
                <Switch checked={form.roles.includes(r.key)} onCheckedChange={() => toggleRole(r.key)} data-testid={`ur-role-opt-${r.key}`} />
                {r.name}
              </label>
            ))}
          </div>
        </div>
        {isCustomer && (
          <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Linked customer ID</span>
            <Input value={form.customer_id || ""} onChange={(e) => set("customer_id", e.target.value)} placeholder="CUST-DEMO-4821" className="mt-1" data-testid="ur-user-customerid" /></label>
        )}
        <div className="flex items-center gap-2"><Toggle checked={form.active} onChange={(v) => set("active", v)} /><span className="text-sm text-slate-600">Active</span></div>
      </AsideSection>
      {!form._new && <MarketingActivityCard staffId={form.id} />}
    </ModalShell>
  );
}

function PermsModal({ user, onClose, roleName }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/users/${user.id}/permissions`).then((r) => setData(r.data)); }, [user.id]);
  return (
    <ModalShell title={`Effective permissions — ${user.name}`} onClose={onClose} testid="ur-perms-modal"
      footer={<Button onClick={onClose} className="bg-indigo-600 hover:bg-indigo-700 text-white">Close</Button>}>
      {!data ? <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
        <div>
          <div className="flex flex-wrap gap-1 mb-3">{data.roles.map((r) => <span key={r} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{roleName(r)}</span>)}</div>
          <p className="text-xs text-slate-400 mb-2">Data scope: <span className="font-semibold text-slate-600">{data.data_scope}</span> · {data.permissions.length} permissions</p>
          <div className="flex flex-wrap gap-1">{data.permissions.map((p) => <code key={p} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{p}</code>)}</div>
        </div>
      )}
    </ModalShell>
  );
}

function RoleModal({ role, groups, onClose, onSaved }) {
  const [perms, setPerms] = useState(new Set(role.permissions));
  const [name, setName] = useState(role.name);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setPerms((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleGroup = (g) => setPerms((s) => {
    const n = new Set(s); const all = g.permissions.every((p) => n.has(p.key));
    g.permissions.forEach((p) => all ? n.delete(p.key) : n.add(p.key)); return n;
  });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/roles/${role.key}`, { name, description: role.description, permissions: [...perms], data_scope: role.data_scope });
      toast.success("Role updated"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save role"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Edit role — ${role.name}`} onClose={onClose} testid="ur-role-modal"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ur-role-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save role</Button></>}>
      <label className="block mb-4"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role name</span>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></label>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key} className="border border-slate-200 rounded-xl p-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-semibold text-slate-800">{g.label} {g.financial && <span className="text-[10px] text-rose-500 uppercase">finance</span>}</span>
              <Switch checked={g.permissions.every((p) => perms.has(p.key))} onCheckedChange={() => toggleGroup(g)} />
            </label>
            <div className="mt-2 grid grid-cols-1 gap-1">
              {g.permissions.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch checked={perms.has(p.key)} onCheckedChange={() => toggle(p.key)} /> {p.label} <code className="text-[10px] text-slate-400">{p.key}</code>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}
